import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import React from "react";

import { Markdown } from "../../../../molecules/markdown/markdown";
import { Message, MessageButton } from "../../chatbot";

export interface ChatMessageProps {
  message: Message;
  onButtonClick?: (button: MessageButton) => void;
  containerWidth?: number;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onButtonClick,
  containerWidth = 400,
}) => {
  const chatbotName = "Chatbot";
  const userName = "User";
  const theme = useTheme();

  const maxBubbleWidth = Math.max(250, containerWidth - 100);

  return (
    <Box
      data-testid="ChatbotMessage"
      sx={{
        display: "flex",
        justifyContent: message.role === "user" ? "flex-end" : "flex-start",
        width: "100%",
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: `${maxBubbleWidth}px`,
          minWidth: "120px",
        }}
      >
        <Typography
          sx={{
            color: "rgba(0, 0, 0, 0.6)",
            fontSize: "11px",
            fontWeight: 500,
            textAlign: message.role === "user" ? "right" : "left",
            mb: 0.5,
            px: 0.5,
          }}
        >
          {message.role === "assistant"
            ? `${chatbotName} • ${message.time ?? ""}`
            : `${message.time ?? ""} • ${userName}`}
        </Typography>

        <Box
          sx={{
            alignItems: "flex-start",
            display: "flex",
            flexDirection: "column",
            fontSize: "14px",
            lineHeight: "1.5",
            width: "100%",
            p: 2,
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",

            ...(message.role === "assistant" && {
              borderRadius: "0px 12px 12px 12px",
              backgroundColor: theme.palette.secondary.main,
            }),

            ...(message.role === "user" && {
              backgroundColor: "#FFF",
              border: "1px solid #ECECEC",
              borderRadius: "12px 0px 12px 12px",
            }),
          }}
        >
          <Box
            sx={{
              mb: message.buttons && message.buttons.length > 0 ? 1 : 0,
              width: "100%",
              wordWrap: "break-word",
            }}
          >
            {message.role === "assistant" ? (
              <Markdown fontSizeRelativeTo="parent">{message.content}</Markdown>
            ) : (
              message.content
            )}
          </Box>

          {message.buttons && message.buttons.length > 0 && onButtonClick && (
            <Stack spacing={1} sx={{ width: "100%" }}>
              {message.buttons.map((button) => (
                <Button
                  key={button.id}
                  variant="outlined"
                  size="small"
                  onClick={() => onButtonClick(button)}
                  sx={{
                    fontSize: "12px",
                    textTransform: "none",
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    width: "100%",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                    },
                    justifyContent: "flex-start",
                  }}
                >
                  {button.label}
                </Button>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessage;
